import { useMonarchRule } from "@/monarch/rules/MonarchRulesProvider";
import { usePathname } from "next/navigation";
import type {
  HubCardReorderData,
  HubCardReorderVariant,
  HubCardReorderTestResult,
  UseHubCardReorder,
} from "./types";

const CUSTOM_RULE_ID = "hubPages";

/**
 * Hook for the hub card reorder experiment.
 * This experiment can return one of two variants:
 * - control: Original card order
 * - reordered: New card order
 */
const useHubCardReorder: UseHubCardReorder = () => {
  // Call the Monarch API to get the experiment result
  const { isLoading, value } = useMonarchRule<{
    data: HubCardReorderData;
    variantCustomId: HubCardReorderVariant;
  }>(CUSTOM_RULE_ID, {});

  // If the path is not /masters or /masters/online-mba, return the control variant
  const pathname = usePathname();
  if (!(pathname === "/masters" || pathname === "/masters/online-mba")) {
    return {
      isLoading: false,
      variant: "control",
    } as HubCardReorderTestResult;
  }

  // If still loading or no value returned, return loading state
  if (isLoading || !value) {
    return {
      isLoading,
    } as HubCardReorderTestResult;
  }

  const variant = value.variantCustomId === "control" ? "control" : "reordered";

  // Return the experiment result with proper typing
  return {
    isLoading: false,
    variant,
    data: value.data,
  };
};

export default useHubCardReorder;
